import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsSplitLayout } from '@/hooks/useMediaQuery';
import { useTenantStore } from '@/store/tenantStore';
import { FilterDropdown, SortDropdown } from '@/features/jobs/components/JobFilters';
import { getSupportedFilterKeys } from '@/features/jobs/utils/filterState';
import { JobCardSkeletons, JobDetailSkeleton, JobResultsGhost } from '@/features/jobs/components/JobSkeletons';
import { EmptyState } from '@/components/feedback/ResultStates';
import type { JobEntity, Aggregation, AggregationValue } from '@/types/job.types';
import { HomeJobListItem } from './HomeJobListItem';
import { HomeJobDetailPanel } from './HomeJobDetailPanel';
import styles from './ExploreJobsSection.module.css';

/**
 * ExploreJobsSection — job results as a list/detail layout.
 * Top: Flutter's "Explore Jobs" heading + All/Hot, filter toggle and filter panel. They sit above
 * the split so they keep their place whether the results are loading, empty or loaded.
 * Left: the job list (infinite scroll, as in Flutter). Right (desktop): sticky preview of the
 * selected job. Below the split breakpoint the preview is hidden, selecting a job opens its page
 * and each card carries Flutter's Apply / Easy Apply / Applied actions.
 * Loading shows card-shaped skeletons; an empty result shows the same skeleton, faded,
 * behind the message.
 */

interface ExploreJobsSectionProps {
  entities: JobEntity[];
  aggregations: Aggregation[];
  exploreTabIndex: number;
  allJobsCount: number;
  hotJobsCount: number;
  isFilterVisible: boolean;
  appliedFilterCount: number;
  isLoadingJobs: boolean;
  isLoadingMore: boolean;
  onSelectTab: (index: number) => void;
  onToggleFilterPanel: () => void;
  onSaveJob: (entity: JobEntity, index: number) => void;
  onUnsaveJob: (entity: JobEntity, index: number) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  onFilterSelection: (values: AggregationValue[]) => void;
  onSelectSort: (value: AggregationValue) => void;
  onClearFilter: (key: string) => void;
  onClickJobTitle: (entity: JobEntity, index: number) => void;
  onClickApply: (entity: JobEntity, index: number, isEasyApply: boolean) => void;
}

const SHIMMER_JOBS = 4;

export function ExploreJobsSection({
  entities,
  aggregations,
  exploreTabIndex,
  allJobsCount,
  hotJobsCount,
  isFilterVisible,
  appliedFilterCount,
  isLoadingJobs,
  isLoadingMore,
  onSelectTab,
  onToggleFilterPanel,
  onSaveJob,
  onUnsaveJob,
  onApplyFilters,
  onClearFilters,
  onFilterSelection,
  onSelectSort,
  onClearFilter,
  onClickJobTitle,
  onClickApply,
}: ExploreJobsSectionProps) {
  const { t } = useTranslation();
  const preferences = useTenantStore((s) => s.preferences);
  const enableEasyApply = !!(useTenantStore((s) => s.config) as Record<string, unknown> | null)
    ?.enableEasyApply;
  const isSplit = useIsSplitLayout();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Flutter FilterContainer renders only the supported multi-filters
  const supportedKeys = getSupportedFilterKeys(preferences as Record<string, unknown> | null);
  const filterAggregations = aggregations.filter((a) => supportedKeys.includes(a.name ?? ''));
  const sortAggregation = aggregations.find((a) => a.name === 'sortBy');

  // Flutter: filter toggle + filter rows need aggregations and at least one job
  const hasFilterData = aggregations.length > 0 && entities.length > 0;
  const showFilters = isFilterVisible && hasFilterData;

  // Keep a valid selection: default to the first job, and fall back when results change
  const selectedIndex = Math.max(
    0,
    entities.findIndex((e) => (e.displayId ?? e.id) === selectedId),
  );
  const selected = entities[selectedIndex];
  const isEmpty = !isLoadingJobs && entities.length === 0;

  const handleSelect = (entity: JobEntity, index: number) => {
    if (!isSplit) {
      onClickJobTitle(entity, index);
      return;
    }
    setSelectedId(entity.displayId ?? entity.id ?? null);
  };

  return (
    <section className={styles.section}>
      <div className={styles.toolbar}>
        {/* Flutter WebCareerPageConstants.exploreJobs */}
        <div className={styles.heading}>
          <span className={styles.accent} />
          <h2 className={styles.title}>{t('Explore Jobs')}</h2>
        </div>

        <div className={styles.toolbarActions}>
          <div className={styles.segment} role="tablist">
            <button
              role="tab"
              aria-selected={exploreTabIndex === 0}
              className={`${styles.segmentBtn} ${exploreTabIndex === 0 ? styles.segmentActive : ''}`}
              onClick={() => onSelectTab(0)}
            >
              {t('All Jobs')}
              {allJobsCount > 0 && <span className={styles.segmentCount}>{allJobsCount}</span>}
            </button>
            <button
              role="tab"
              aria-selected={exploreTabIndex === 1}
              className={`${styles.segmentBtn} ${exploreTabIndex === 1 ? styles.segmentActive : ''}`}
              onClick={() => onSelectTab(1)}
            >
              {t('Hot Jobs')}
              {hotJobsCount > 0 && <span className={styles.segmentCount}>{hotJobsCount}</span>}
            </button>
          </div>

          {hasFilterData && (
            // Flutter FilterIconButton(isSelected: appliedFilterCount > 0); the row starts open
            <button
              className={`${styles.filterToggle} ${appliedFilterCount > 0 ? styles.filterActive : ''}`}
              onClick={onToggleFilterPanel}
              aria-label={t('Toggle filters')}
              aria-expanded={isFilterVisible}
              title={t('Filters')}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 5h14M5 10h10M7 15h6" strokeLinecap="round" />
              </svg>
              {appliedFilterCount > 0 && (
                <span className={styles.filterBadge}>{appliedFilterCount}</span>
              )}
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className={styles.filterPanel}>
          <div className={styles.filterChips}>
            {filterAggregations.map((agg) => (
              <FilterDropdown
                key={agg.name}
                aggregation={agg}
                onChange={onFilterSelection}
                onApply={onApplyFilters}
                onClear={onClearFilter}
              />
            ))}
          </div>
          <div className={styles.filterFooter}>
            {sortAggregation && (
              <SortDropdown aggregation={sortAggregation} onSelect={onSelectSort} />
            )}
            <div className={styles.filterActions}>
              <button className={styles.filterActionBtn} onClick={onClearFilters}>
                {t('Clear all')}
              </button>
              <button
                className={`${styles.filterActionBtn} ${styles.filterApply}`}
                onClick={onApplyFilters}
              >
                {t('Apply Filters')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEmpty ? (
        // Flutter GenericWidget.noRecords()
        <EmptyState message={t('No Records')} ghost={<JobResultsGhost />} />
      ) : (
        <div className={styles.split}>
          {/* ── Left: job list ── */}
          <div className={styles.listCol}>
            <div className={styles.list}>
              {isLoadingJobs ? (
                <JobCardSkeletons count={SHIMMER_JOBS} />
              ) : (
                <>
                  {entities.map((entity, index) => (
                    <HomeJobListItem
                      key={entity.displayId ?? entity.id ?? index}
                      entity={entity}
                      isSelected={isSplit && index === selectedIndex}
                      onSelect={() => handleSelect(entity, index)}
                      onSave={() => onSaveJob(entity, index)}
                      onUnsave={() => onUnsaveJob(entity, index)}
                      enableEasyApply={enableEasyApply}
                      onApply={(isEasyApply) => onClickApply(entity, index, isEasyApply)}
                    />
                  ))}
                  {isLoadingMore && <JobCardSkeletons count={2} />}
                </>
              )}
            </div>
          </div>

          {/* ── Right: selected job preview (desktop) ── */}
          {isSplit && (
            <div className={styles.detailCol}>
              {isLoadingJobs ? (
                <JobDetailSkeleton />
              ) : selected ? (
                <HomeJobDetailPanel
                  key={selected.displayId ?? selected.id}
                  entity={selected}
                  onSave={() => onSaveJob(selected, selectedIndex)}
                  onUnsave={() => onUnsaveJob(selected, selectedIndex)}
                  onClickApply={(isEasyApply) => onClickApply(selected, selectedIndex, isEasyApply)}
                  onViewDetails={() => onClickJobTitle(selected, selectedIndex)}
                />
              ) : null}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
